import React, { useEffect, useState } from 'react';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import StradaSpinner from 'shared-components/components/StradaSpinner';
import { useSelector } from 'react-redux';
import type { RootState } from 'mainStore';

export default function AmountBudgetedBar(): JSX.Element {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isFocused, setIsFocused] = useState(false);
  const [amount, setAmount] = useState(NaN);
  const sideSheetData = useSelector((state: RootState) => state.workspaces.sideSheetData.sideSheetData);

  useEffect(() => {
    if (sideSheetData !== null) {
      if (sideSheetData.amount_budget !== null) {
        setAmount(sideSheetData.amount_budget);
      } else {
        setAmount(NaN);
      }
    } else {
      setAmount(NaN);
    }
  }, [sideSheetData]);

  const { mutate: updateAmountBudget, isLoading } = useMutation(async () => axios({
    url: `/api/budget-calendar/event/${sideSheetData?.id}/`,
    method: 'PATCH',
    data: {
      // eslint-disable-next-line use-isnan
      amount_budget: Number(amount) === NaN ? 0 : amount,
    },
  }), {
    onSuccess: async (res): Promise<void> => {
      await queryClient.invalidateQueries('others-events').then();
      await queryClient.invalidateQueries('sidesheet/get-events').then();
      await queryClient.invalidateQueries('user-assigned-events').then();
      await queryClient.invalidateQueries('prioritized-events').then();
      await queryClient.invalidateQueries('allevents/get-events').then();
      await queryClient.invalidateQueries('user-created-events').then();
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      res.data.amount_budget !== null && enqueueSnackbar('Amount budgeted updated successfully');
    },
  });

  const handleEnterPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      updateAmountBudget();
    }
  };

  const handleConditionalText = (): JSX.Element => {
    if (isFocused || !Number.isNaN(amount)) {
      return <>$</>;
    }
    return <HorizontalRuleIcon />;
  };

  return (
    <div className='assignee-sheet-bar'>
      <h6 className='side-sheet-side-label'> Amount Budgeted </h6>
      <div className='assignee-sheet-popover'>
        <div className='popover-btn' aria-hidden='true'>
          {isLoading && amount ? <StradaSpinner open={isLoading} message='Updating' /> : (
            <div className='input-bar'>
              {handleConditionalText()}
              <input
                value={String(amount)}
                onChange={(e): void => { setAmount(e.target.valueAsNumber); handleConditionalText(); }}
                onFocus={(): void => { setIsFocused(true); }}
                onBlur={(): void => { setIsFocused(false); updateAmountBudget(); }}
                onKeyPress={handleEnterPress}
                type='number'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
